import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, Chip, Collapse, TextField } from '@mui/material';
import { ChevronLeft, ChevronRight, Today, ZoomIn, ZoomOut, ExpandMore, ExpandLess, Person, Diamond, Edit, Delete } from '@mui/icons-material';
import { useBrandColors } from '../hooks/useBrandColors';

// Hours per day constant
const HOURS_PER_DAY = 8;

// Types
export interface GanttTeamMember {
  id: string;
  name: string;
  hours: number;
  startDate: string;
  endDate: string;
  color?: string;
}

export interface GanttPhase {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'pending' | 'in_progress' | 'completed';
  teamMembers: GanttTeamMember[];
}

export interface GanttKeystone {
  id: string;
  name: string;
  date: string;
  type: 'review' | 'hand_in' | 'demo' | 'release' | 'go_live';
}

interface GanttChartProps {
  phases: GanttPhase[];
  keystones: GanttKeystone[];
  onPhaseUpdate?: (phase: GanttPhase) => void;
  onMemberUpdate?: (phaseId: string, member: GanttTeamMember) => void;
  onKeystoneUpdate?: (keystone: GanttKeystone) => void;
  onKeystoneEdit?: (keystone: GanttKeystone) => void;
  onKeystoneDelete?: (keystoneId: string) => void;
}

// Helper functions
const parseDate = (dateStr: string | null | undefined): Date => {
  // Handle null/undefined/empty
  if (!dateStr) {
    return new Date(); // Return today as fallback
  }
  
  // Handle ISO format (2026-01-10T00:00:00.000Z)
  if (dateStr.includes('T')) {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? new Date() : d;
  }
  
  // Handle YYYY-MM-DD format
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts.map(Number);
    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
      return new Date(year, month - 1, day);
    }
  }
  
  // Try native parsing as fallback
  const parsed = new Date(dateStr);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
};

const formatDate = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const getDaysBetween = (start: Date, end: Date): number => {
  const diffTime = end.getTime() - start.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

// Status colors
const getStatusColor = (status: string): string => {
  switch (status) {
    case 'completed': return '#27ae60';
    case 'in_progress': return '#3498db';
    case 'pending': return '#95a5a6';
    default: return '#95a5a6';
  }
};

const getStatusBgColor = (status: string): string => {
  switch (status) {
    case 'completed': return '#d4edda';
    case 'in_progress': return '#cce5ff';
    case 'pending': return '#f8f9fa';
    default: return '#f8f9fa';
  }
};

// Keystone colors
const getKeystoneColor = (type: string): string => {
  switch (type) {
    case 'review': return '#3498db';
    case 'hand_in': return '#e74c3c';
    case 'demo': return '#9b59b6';
    case 'release': return '#27ae60';
    case 'go_live': return '#f39c12';
    default: return '#9b59b6';
  }
};

export const GanttChart: React.FC<GanttChartProps> = ({
  phases,
  keystones,
  onPhaseUpdate,
  onMemberUpdate,
  onKeystoneUpdate,
  onKeystoneEdit,
  onKeystoneDelete,
}) => {
  const brandColors = useBrandColors();
  const containerRef = useRef<HTMLDivElement>(null);
  const [expandedPhases, setExpandedPhases] = useState<Record<string, boolean>>({});
  const [editingPhaseId, setEditingPhaseId] = useState<string | null>(null);
  const [editingPhaseName, setEditingPhaseName] = useState<string>('');
  const editInputRef = useRef<HTMLInputElement>(null);
  
  const [viewStart, setViewStart] = useState<Date>(() => {
    const allDates: Date[] = [];
    phases.forEach(p => {
      allDates.push(parseDate(p.startDate));
      p.teamMembers.forEach(m => allDates.push(parseDate(m.startDate)));
    });
    keystones.forEach(k => allDates.push(parseDate(k.date)));
    
    if (allDates.length > 0) {
      const earliest = new Date(Math.min(...allDates.map(d => d.getTime())));
      return addDays(earliest, -3);
    }
    return addDays(new Date(), -7);
  });
  
  const [daysToShow, setDaysToShow] = useState(42);
  
  const [dragging, setDragging] = useState<{
    type: 'phase' | 'member' | 'keystone';
    id: string;
    phaseId?: string;
    dragType: 'move' | 'resize-start' | 'resize-end';
    startX: number;
    originalStart: string;
    originalEnd?: string;
  } | null>(null);

  const viewEnd = addDays(viewStart, daysToShow);

  // Generate day headers
  const dayHeaders: { date: Date; label: string; isWeekend: boolean; isToday: boolean }[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  for (let i = 0; i < daysToShow; i++) {
    const date = addDays(viewStart, i);
    dayHeaders.push({
      date,
      label: `${date.getDate()}`,
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      isToday: date.toDateString() === today.toDateString(),
    });
  }

  // Generate month headers
  const monthHeaders: { month: string; span: number }[] = [];
  let currentMonth = '';
  let currentSpan = 0;
  
  dayHeaders.forEach((day) => {
    const monthStr = day.date.toLocaleString('default', { month: 'short' });
    if (monthStr !== currentMonth) {
      if (currentMonth) monthHeaders.push({ month: currentMonth, span: currentSpan });
      currentMonth = monthStr;
      currentSpan = 1;
    } else {
      currentSpan++;
    }
  });
  if (currentMonth) monthHeaders.push({ month: currentMonth, span: currentSpan });

  // Calculate bar position
  const getBarStyle = (startDate: string, endDate: string) => {
    const start = parseDate(startDate);
    const end = parseDate(endDate);
    const startOffset = getDaysBetween(viewStart, start);
    const duration = getDaysBetween(start, end) + 1;
    const cellWidth = 100 / daysToShow;
    
    return {
      left: `${Math.max(0, startOffset * cellWidth)}%`,
      width: `${Math.min(100 - Math.max(0, startOffset * cellWidth), Math.max(0, duration * cellWidth))}%`,
    };
  };

  // Calculate keystone position
  const getKeystonePosition = (date: string) => {
    const d = parseDate(date);
    const offset = getDaysBetween(viewStart, d);
    return `${((offset + 0.5) / daysToShow) * 100}%`;
  };

  // Toggle phase expand
  const togglePhase = (phaseId: string) => {
    setExpandedPhases(prev => ({ ...prev, [phaseId]: !prev[phaseId] }));
  };

  // Navigation
  const scrollLeft = () => setViewStart(addDays(viewStart, -7));
  const scrollRight = () => setViewStart(addDays(viewStart, 7));
  const goToToday = () => setViewStart(addDays(new Date(), -7));
  const zoomIn = () => setDaysToShow(Math.max(14, daysToShow - 7));
  const zoomOut = () => setDaysToShow(Math.min(90, daysToShow + 7));

  // Drag handlers
  const handleMouseDown = (
    e: React.MouseEvent,
    type: 'phase' | 'member' | 'keystone',
    id: string,
    dragType: 'move' | 'resize-start' | 'resize-end',
    startDate: string,
    endDate?: string,
    phaseId?: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging({
      type,
      id,
      phaseId,
      dragType,
      startX: e.clientX,
      originalStart: startDate,
      originalEnd: endDate,
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging || !containerRef.current) return;

    const containerWidth = containerRef.current.getBoundingClientRect().width - 180; // Subtract label width
    const deltaX = e.clientX - dragging.startX;
    const dayWidth = containerWidth / daysToShow;
    const daysDelta = Math.round(deltaX / dayWidth);

    if (daysDelta === 0) return;

    if (dragging.type === 'keystone') {
      const keystone = keystones.find(k => k.id === dragging.id);
      if (keystone && onKeystoneUpdate) {
        onKeystoneUpdate({
          ...keystone,
          date: formatDate(addDays(parseDate(dragging.originalStart), daysDelta)),
        });
      }
    } else if (dragging.type === 'phase') {
      const phase = phases.find(p => p.id === dragging.id);
      if (phase && onPhaseUpdate) {
        let newStart = dragging.originalStart;
        let newEnd = dragging.originalEnd || phase.endDate;

        if (dragging.dragType === 'move') {
          newStart = formatDate(addDays(parseDate(dragging.originalStart), daysDelta));
          newEnd = formatDate(addDays(parseDate(dragging.originalEnd!), daysDelta));
        } else if (dragging.dragType === 'resize-start') {
          newStart = formatDate(addDays(parseDate(dragging.originalStart), daysDelta));
          if (parseDate(newStart) >= parseDate(newEnd)) newStart = formatDate(addDays(parseDate(newEnd), -1));
        } else if (dragging.dragType === 'resize-end') {
          newEnd = formatDate(addDays(parseDate(dragging.originalEnd!), daysDelta));
          if (parseDate(newEnd) <= parseDate(newStart)) newEnd = formatDate(addDays(parseDate(newStart), 1));
        }

        onPhaseUpdate({ ...phase, startDate: newStart, endDate: newEnd });
      }
    } else if (dragging.type === 'member') {
      const phase = phases.find(p => p.id === dragging.phaseId);
      const member = phase?.teamMembers.find(m => m.id === dragging.id);
      if (member && phase && onMemberUpdate) {
        let newStart = dragging.originalStart;
        let newEnd = dragging.originalEnd || member.endDate;

        if (dragging.dragType === 'move') {
          newStart = formatDate(addDays(parseDate(dragging.originalStart), daysDelta));
          newEnd = formatDate(addDays(parseDate(dragging.originalEnd!), daysDelta));
        } else if (dragging.dragType === 'resize-start') {
          newStart = formatDate(addDays(parseDate(dragging.originalStart), daysDelta));
          if (parseDate(newStart) >= parseDate(newEnd)) newStart = formatDate(addDays(parseDate(newEnd), -1));
        } else if (dragging.dragType === 'resize-end') {
          newEnd = formatDate(addDays(parseDate(dragging.originalEnd!), daysDelta));
          if (parseDate(newEnd) <= parseDate(newStart)) newEnd = formatDate(addDays(parseDate(newStart), 1));
        }

        onMemberUpdate(phase.id, { ...member, startDate: newStart, endDate: newEnd });
      }
    }
  };

  const handleMouseUp = () => setDragging(null);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);

  // Calculate total height for keystone lines
  const totalRows = phases.reduce((acc, phase) => {
    return acc + 1 + (expandedPhases[phase.id] ? phase.teamMembers.length : 0);
  }, 0);
  const rowHeight = 40;
  const headerHeight = 52;

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Previous week"><IconButton size="small" onClick={scrollLeft}><ChevronLeft /></IconButton></Tooltip>
          <Tooltip title="Go to today"><IconButton size="small" onClick={goToToday}><Today /></IconButton></Tooltip>
          <Tooltip title="Next week"><IconButton size="small" onClick={scrollRight}><ChevronRight /></IconButton></Tooltip>
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {viewStart.toLocaleDateString('default', { month: 'short', day: 'numeric' })} - {viewEnd.toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Zoom in"><IconButton size="small" onClick={zoomIn}><ZoomIn /></IconButton></Tooltip>
          <Typography variant="caption">{daysToShow} days</Typography>
          <Tooltip title="Zoom out"><IconButton size="small" onClick={zoomOut}><ZoomOut /></IconButton></Tooltip>
        </Box>
      </Box>

      {/* Gantt Chart */}
      <Box ref={containerRef} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden', position: 'relative' }}>
        {/* Month Headers */}
        <Box sx={{ display: 'flex', borderBottom: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
          <Box sx={{ width: 180, minWidth: 180, borderRight: '1px solid #e0e0e0', p: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>Phase / Member</Typography>
          </Box>
          <Box sx={{ flex: 1, display: 'flex' }}>
            {monthHeaders.map((header, idx) => (
              <Box key={idx} sx={{ width: `${(header.span / daysToShow) * 100}%`, textAlign: 'center', borderRight: '1px solid #e0e0e0', p: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: brandColors.primary }}>{header.month}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Day Headers */}
        <Box sx={{ display: 'flex', borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ width: 180, minWidth: 180, borderRight: '1px solid #e0e0e0' }} />
          <Box sx={{ flex: 1, display: 'flex', position: 'relative' }}>
            {dayHeaders.map((day, idx) => (
              <Box key={idx} sx={{ flex: 1, textAlign: 'center', p: 0.5, backgroundColor: day.isToday ? brandColors.primary + '20' : day.isWeekend ? '#f8f9fa' : '#fff', borderRight: '1px solid #f0f0f0' }}>
                <Typography variant="caption" sx={{ fontWeight: day.isToday ? 700 : 400, color: day.isToday ? brandColors.primary : day.isWeekend ? '#999' : '#666', fontSize: '10px' }}>{day.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Keystone Lines (overlay) - stretch full height */}
        {keystones.map((keystone) => {
          const position = getKeystonePosition(keystone.date);
          const color = getKeystoneColor(keystone.type);
          return (
            <Box
              key={keystone.id}
              sx={{
                position: 'absolute',
                left: `calc(180px + (100% - 180px) * ${parseFloat(position) / 100})`,
                top: headerHeight,
                bottom: 0,
                width: 2,
                backgroundColor: color,
                zIndex: 20,
                cursor: 'ew-resize',
                '&:hover': { width: 4 },
              }}
              onMouseDown={(e) => handleMouseDown(e, 'keystone', keystone.id, 'move', keystone.date)}
            >
              {/* Diamond marker at bottom with hover actions */}
              <Box sx={{
                position: 'absolute',
                bottom: 10,
                left: -20,
                width: 40,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                '&:hover .keystone-actions': { opacity: 1 },
              }}>
                {/* Edit/Delete actions */}
                <Box 
                  className="keystone-actions"
                  sx={{ 
                    display: 'flex', 
                    gap: 0.25, 
                    mb: 0.5, 
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    backgroundColor: '#fff',
                    borderRadius: 1,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                    p: 0.25,
                  }}
                >
                  {onKeystoneEdit && (
                    <Tooltip title="Redigera">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onKeystoneEdit(keystone);
                        }}
                        sx={{ p: 0.25, color: '#3498db' }}
                      >
                        <Edit sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onKeystoneDelete && (
                    <Tooltip title="Ta bort">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onKeystoneDelete(keystone.id);
                        }}
                        sx={{ p: 0.25, color: '#e74c3c' }}
                      >
                        <Delete sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {/* Diamond */}
                <Tooltip title={`${keystone.name} - Drag to move`}>
                  <Box sx={{
                    width: 18,
                    height: 18,
                    backgroundColor: color,
                    transform: 'rotate(45deg)',
                    cursor: 'grab',
                    border: '2px solid #fff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }} />
                </Tooltip>
                {/* Label */}
                <Typography variant="caption" sx={{
                  mt: 0.5,
                  fontSize: '9px',
                  fontWeight: 600,
                  color,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 80,
                  textAlign: 'center',
                }}>
                  {keystone.name}
                </Typography>
              </Box>
            </Box>
          );
        })}

        {/* Phases */}
        {phases.map((phase) => (
          <Box key={phase.id}>
            {/* Phase Row */}
            <Box sx={{ display: 'flex', '&:hover': { backgroundColor: '#fafafa' } }}>
              <Box sx={{ width: 180, minWidth: 180, borderRight: '1px solid #e0e0e0', p: 1, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={() => togglePhase(phase.id)}>
                {expandedPhases[phase.id] ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: getStatusColor(phase.status) }} />
                {editingPhaseId === phase.id ? (
                  <TextField
                    inputRef={editInputRef}
                    value={editingPhaseName}
                    onChange={(e) => setEditingPhaseName(e.target.value)}
                    onBlur={() => {
                      if (editingPhaseName.trim() && editingPhaseName !== phase.name && onPhaseUpdate) {
                        onPhaseUpdate({ ...phase, name: editingPhaseName.trim() });
                      }
                      setEditingPhaseId(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (editingPhaseName.trim() && editingPhaseName !== phase.name && onPhaseUpdate) {
                          onPhaseUpdate({ ...phase, name: editingPhaseName.trim() });
                        }
                        setEditingPhaseId(null);
                      } else if (e.key === 'Escape') {
                        setEditingPhaseId(null);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    autoFocus
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        fontSize: 13, 
                        fontWeight: 600, 
                        py: 0.5,
                        px: 1,
                      },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      }
                    }}
                  />
                ) : (
                  <Tooltip title="Dubbelklicka för att redigera">
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: 13,
                        cursor: 'text',
                        '&:hover': { textDecoration: 'underline dotted' },
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        setEditingPhaseId(phase.id);
                        setEditingPhaseName(phase.name);
                      }}
                    >
                      {phase.name}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ flex: 1, position: 'relative', height: rowHeight, display: 'flex', alignItems: 'center' }}>
                {/* Grid lines */}
                {dayHeaders.map((day, idx) => (
                  <Box key={idx} sx={{ position: 'absolute', left: `${(idx / daysToShow) * 100}%`, width: `${100 / daysToShow}%`, height: '100%', borderRight: '1px solid #f0f0f0', backgroundColor: day.isToday ? brandColors.primary + '10' : day.isWeekend ? '#fafafa' : 'transparent' }} />
                ))}
                {/* Today line */}
                {dayHeaders.findIndex(d => d.isToday) >= 0 && (
                  <Box sx={{ position: 'absolute', left: `${((dayHeaders.findIndex(d => d.isToday) + 0.5) / daysToShow) * 100}%`, width: 2, height: '100%', backgroundColor: '#e74c3c', zIndex: 5 }} />
                )}
                {/* Phase Bar */}
                <Box
                  sx={{
                    position: 'absolute',
                    ...getBarStyle(phase.startDate, phase.endDate),
                    height: 28,
                    backgroundColor: getStatusBgColor(phase.status),
                    border: `2px solid ${getStatusColor(phase.status)}`,
                    borderRadius: 1,
                    cursor: 'grab',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
                  }}
                  onMouseDown={(e) => handleMouseDown(e, 'phase', phase.id, 'move', phase.startDate, phase.endDate)}
                >
                  <Box sx={{ position: 'absolute', left: 0, top: 0, width: 8, height: '100%', cursor: 'ew-resize', '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' } }} onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, 'phase', phase.id, 'resize-start', phase.startDate, phase.endDate); }} />
                  <Typography variant="caption" sx={{ fontSize: '10px', fontWeight: 600 }}>{phase.name}</Typography>
                  <Box sx={{ position: 'absolute', right: 0, top: 0, width: 8, height: '100%', cursor: 'ew-resize', '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' } }} onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, 'phase', phase.id, 'resize-end', phase.startDate, phase.endDate); }} />
                </Box>
              </Box>
            </Box>

            {/* Team Members (Expandable) */}
            <Collapse in={expandedPhases[phase.id]}>
              {phase.teamMembers.map((member) => (
                <Box key={member.id} sx={{ display: 'flex', backgroundColor: '#fafafa' }}>
                  <Box sx={{ width: 180, minWidth: 180, borderRight: '1px solid #e0e0e0', pl: 4, pr: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person sx={{ fontSize: 16, color: '#666' }} />
                    <Typography variant="caption">{member.name}</Typography>
                    <Typography variant="caption" color="textSecondary">({member.hours}h)</Typography>
                  </Box>
                  <Box sx={{ flex: 1, position: 'relative', height: rowHeight, display: 'flex', alignItems: 'center' }}>
                    {/* Grid lines */}
                    {dayHeaders.map((day, idx) => (
                      <Box key={idx} sx={{ position: 'absolute', left: `${(idx / daysToShow) * 100}%`, width: `${100 / daysToShow}%`, height: '100%', borderRight: '1px solid #f0f0f0', backgroundColor: day.isToday ? brandColors.primary + '05' : day.isWeekend ? '#f8f8f8' : 'transparent' }} />
                    ))}
                    {/* Member Bar - height based on hours */}
                    {(() => {
                      // Calculate bar height: 8px minimum, 28px maximum, proportional to hours
                      const minHeight = 8;
                      const maxHeight = 28;
                      const maxHours = 80; // 80+ hours = max height
                      const barHeight = Math.min(maxHeight, minHeight + (Math.min(member.hours, maxHours) / maxHours) * (maxHeight - minHeight));
                      return (
                        <Box
                          sx={{
                            position: 'absolute',
                            ...getBarStyle(member.startDate, member.endDate),
                            height: barHeight,
                            backgroundColor: brandColors.primary + '40',
                            border: `1px solid ${brandColors.primary}`,
                            borderRadius: 0.5,
                            cursor: 'grab',
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': { boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
                          }}
                          onMouseDown={(e) => handleMouseDown(e, 'member', member.id, 'move', member.startDate, member.endDate, phase.id)}
                        >
                          <Box sx={{ position: 'absolute', left: 0, top: 0, width: 6, height: '100%', cursor: 'ew-resize' }} onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, 'member', member.id, 'resize-start', member.startDate, member.endDate, phase.id); }} />
                          <Box sx={{ position: 'absolute', right: 0, top: 0, width: 6, height: '100%', cursor: 'ew-resize' }} onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, 'member', member.id, 'resize-end', member.startDate, member.endDate, phase.id); }} />
                        </Box>
                      );
                    })()}
                  </Box>
                </Box>
              ))}
            </Collapse>
          </Box>
        ))}

        {/* Empty space for keystone labels */}
        <Box sx={{ height: 45 }} />
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 12, height: 12, backgroundColor: '#27ae60', borderRadius: '50%' }} /><Typography variant="caption">Completed</Typography></Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 12, height: 12, backgroundColor: '#3498db', borderRadius: '50%' }} /><Typography variant="caption">In Progress</Typography></Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 12, height: 12, backgroundColor: '#95a5a6', borderRadius: '50%' }} /><Typography variant="caption">Pending</Typography></Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 2, height: 12, backgroundColor: '#e74c3c' }} /><Typography variant="caption">Today</Typography></Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Diamond sx={{ fontSize: 14, color: '#9b59b6' }} /><Typography variant="caption">Keystone</Typography></Box>
      </Box>
    </Box>
  );
};

export default GanttChart;
